// Sugalarm — iOS Live Activity bridge manager (cycle 168).
//
// Thin JS side of the hand-rolled `LiveActivityBridge` Capacitor
// plugin (mobile/ios/App/App/LiveActivityBridge.swift — the cycle-41
// AuthBridge precedent; the published capacitor-live-activity plugin
// requires Capacitor ≥7 and the shell is pinned to the Cap-6 LTS).
// Reached EXCLUSIVELY via `window.Capacitor.Plugins` per the cycle-138
// rule — the pure-remote webview has no bundler for a bare dynamic
// import. No-ops everywhere outside the iOS native shell: web, kiosk,
// PWA, and Android never touch any of this.
//
// Responsibilities (deliverable-4 contract):
//   - after auth + on app-active, start an activity for the active
//     member when none is running (pushType token), seeded from the
//     latest reading;
//   - register the iOS 17.2+ push-to-start token where supported;
//   - forward every token (per-activity `update`, account-level
//     `start`) to POST /api/live-activity/tokens tagged with the
//     active member_id;
//   - end/replace on member switch + sign-out + a feed stale past the
//     tile's window.
//
// DISPLAY-ONLY: the tile carries no alarm control (cycle-73
// invariant); this module never touches the alarm engine.

import {
  deleteLiveActivityToken,
  registerLiveActivityToken,
} from "../api/liveActivity";
import { capacitorPlatform, isCapacitorNative } from "./capacitor";

const BUNDLE_ID = "dev.sugalarm.app";

interface LiveActivityPlugin {
  getInfo: () => Promise<{
    available: boolean;
    enabled: boolean;
    pushToStartSupported: boolean;
    running: boolean;
  }>;
  start: (opts: {
    memberId: string;
    memberName: string;
    units: string;
    state: Record<string, unknown>;
  }) => Promise<{ id: string }>;
  update: (opts: { state: Record<string, unknown> }) => Promise<void>;
  end: () => Promise<void>;
  /** Cycle 211: write the home-screen widget snapshot to the App Group
   *  + reload widget timelines. Same plugin, display-only. */
  writeWidgetSnapshot: (opts: Record<string, unknown>) => Promise<void>;
  registerPushToStart: (opts: {
    memberId: string;
  }) => Promise<{ supported: boolean }>;
  addListener: (
    event: string,
    cb: (data: Record<string, unknown>) => void,
  ) => Promise<{ remove: () => Promise<void> }> | { remove: () => void };
}

function plugin(): LiveActivityPlugin | null {
  if (!isCapacitorNative() || capacitorPlatform() !== "ios") return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.["LiveActivityBridge"];
  return p != null ? (p as LiveActivityPlugin) : null;
}

export interface LiveActivitySeed {
  memberId: string;
  memberName: string;
  /** Display preference — "mg/dL" | "mmol/L" (mapped to the wire's
   *  "mgdl"/"mmol"). */
  units: string;
  mgdl: number | null;
  /** Dexcom trend string (DoubleUp…) or null. */
  trend: string | null;
  zone: string;
  stale: boolean;
  /** Reading timestamp, epoch ms. */
  tsMs: number | null;
  /** Cycle 211 (widget snapshot only — the Live Activity ignores these):
   *  signed delta vs. the prior reading in mg/dL, insulin-on-board in
   *  units, and recent mg/dL points (oldest→newest) for the medium
   *  widget's sparkline. All optional — omitted gracefully when absent. */
  deltaMgdl?: number | null;
  iobU?: number | null;
  recent?: number[];
}

const TREND_MAP: Record<string, string> = {
  DoubleUp: "double_up",
  SingleUp: "single_up",
  FortyFiveUp: "forty_five_up",
  Flat: "flat",
  FortyFiveDown: "forty_five_down",
  SingleDown: "single_down",
  DoubleDown: "double_down",
};

const ZONE_MAP: Record<string, string> = {
  in_range: "in_range",
  low: "low",
  high: "high",
  urgent_low: "urgent_low",
  urgent_high: "urgent_high",
};

let startedForMember: string | null = null;
let listenersWired = false;
let pushToStartWired = false;
/** Tokens registered this session, for sign-out cleanup. */
const registeredTokens: {
  member_id: string;
  kind: "start" | "update";
  token: string;
}[] = [];

function seedState(seed: LiveActivitySeed): Record<string, unknown> {
  return {
    mgdl: seed.mgdl ?? 0,
    trend: TREND_MAP[seed.trend ?? ""] ?? "flat",
    iob_u: null, // the first push update carries the server-computed IOB
    zone: ZONE_MAP[seed.zone] ?? "in_range",
    stale: seed.stale || seed.mgdl == null,
    ts: seed.tsMs != null ? Math.floor(seed.tsMs / 1000) : Math.floor(Date.now() / 1000),
  };
}

function wireTokenListener(p: LiveActivityPlugin): void {
  if (listenersWired) return;
  listenersWired = true;
  void p.addListener("liveActivityPushToken", (data) => {
    const token = typeof data.token === "string" ? data.token : "";
    const kind = data.kind === "start" ? "start" : "update";
    // The bridge threads back the member the activity was started
    // for; fall back to the member we last started.
    const memberId =
      (typeof data.memberId === "string" && data.memberId) ||
      startedForMember ||
      "";
    if (!token || !memberId) return;
    registeredTokens.push({ member_id: memberId, kind, token });
    rememberLiveActivityToken(token);
    void registerLiveActivityToken({
      member_id: memberId,
      kind,
      token,
      bundle_id: BUNDLE_ID,
    });
  });
}

/** Ensure an activity is running for the active member, seeded from
 *  the latest reading. Ends + replaces on member switch. Safe to call
 *  repeatedly (app-active, member change, reading refresh). */
export async function ensureLiveActivity(
  seed: LiveActivitySeed,
): Promise<void> {
  const p = plugin();
  if (!p) return;
  try {
    const info = await p.getInfo();
    if (!info.available || !info.enabled) return;
    wireTokenListener(p);
    if (info.pushToStartSupported && !pushToStartWired) {
      pushToStartWired = true;
      void p.registerPushToStart({ memberId: seed.memberId });
    }
    if (info.running && startedForMember === seed.memberId) {
      // Keep the foregrounded tile fresh locally; pushes are primary.
      void p.update({ state: seedState(seed) });
      return;
    }
    if (info.running && startedForMember !== seed.memberId) {
      await p.end();
    }
    await p.start({
      memberId: seed.memberId,
      memberName: seed.memberName,
      units: seed.units === "mmol/L" ? "mmol" : "mgdl",
      state: seedState(seed),
    });
    startedForMember = seed.memberId;
  } catch (err) {
    console.warn("[liveActivity] ensure failed", err);
  }
}

/** End the running activity (sign-out / stale shutdown) and drop this
 *  session's registered tokens server-side (best-effort). */
export async function endLiveActivity(): Promise<void> {
  const p = plugin();
  if (!p) return;
  try {
    await p.end();
  } catch {
    /* best-effort */
  }
  startedForMember = null;
  const tokens = registeredTokens.splice(0, registeredTokens.length);
  for (const t of tokens) {
    void deleteLiveActivityToken(t);
  }
}

// ─── Cycle 211: home-screen widget snapshot ──────────────────────────
//
// The widget reads the App-Group snapshot the bridge writes here — a
// SEPARATE path from the Live Activity, deliberately NOT gated on
// `areActivitiesEnabled`: a user who turned Live Activities off should
// still get a live home-screen widget. Display-only; no alarm control.
// No-ops off the iOS shell (the plugin is absent on web/kiosk/Android).

/** Build the snake_case widget-snapshot payload from a reading seed. The
 *  SINGLE source of the payload shape — consumed by the iOS writer below
 *  AND the cycle-212 Android `WidgetBridge` dispatch in
 *  `web/src/lib/glucoseWidget.ts`, so the iOS and Android widgets are
 *  seeded from identical data and can never disagree. Optional fields are
 *  omitted when absent so the native side never distinguishes null from
 *  missing (the faces omit them gracefully). */
export function buildWidgetSnapshotPayload(
  seed: LiveActivitySeed,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    signedIn: true,
    mgdl: seed.mgdl ?? 0,
    trend: TREND_MAP[seed.trend ?? ""] ?? "flat",
    zone: ZONE_MAP[seed.zone] ?? "in_range",
    stale: seed.stale || seed.mgdl == null,
    // Cycle 359: server-authoritative signal-loss flag (snake_case, mirrors
    // the widget-endpoint field). At SPA write time this equals `stale` — both
    // read the app's provider-aware staleness (`seed.stale`) — but the native
    // widgets re-derive it from `ts` age at render time against the shorter
    // staleness window (the "No signal · check CGM" face), independent of a
    // fetch, so a closed phone flips honestly. Additive; old shells ignore it.
    signal_lost: seed.stale || seed.mgdl == null,
    ts:
      seed.tsMs != null
        ? Math.floor(seed.tsMs / 1000)
        : Math.floor(Date.now() / 1000),
    memberName: seed.memberName,
    // Cycle 365: the member this snapshot describes. The iOS app's
    // background re-seed needs it to register a push token when it has to
    // START a Live Activity (iOS ends one at its ~8-hour ceiling) — a
    // background wake has no SPA state to ask. Additive: the widget faces
    // and the Android store ignore unknown keys.
    member_id: seed.memberId,
    units: seed.units === "mmol/L" ? "mmol" : "mgdl",
  };
  if (seed.deltaMgdl != null) payload.delta = Math.round(seed.deltaMgdl);
  if (seed.iobU != null) payload.iob_u = seed.iobU;
  if (seed.recent && seed.recent.length >= 2) payload.recent = seed.recent;
  return payload;
}

/** Write the latest reading to the iOS home-screen widget snapshot.
 *  No-ops off the iOS shell (the LiveActivityBridge plugin is absent on
 *  web/kiosk/Android). The cycle-212 per-platform dispatcher
 *  (`glucoseWidget.ts`) routes the iOS branch here and the Android branch
 *  to its own bridge — call THAT, not this, from app code. */
export async function writeGlucoseWidgetSnapshot(
  seed: LiveActivitySeed,
): Promise<void> {
  const p = plugin();
  if (!p) return;
  try {
    await p.writeWidgetSnapshot(buildWidgetSnapshotPayload(seed));
  } catch (err) {
    console.warn("[widget] snapshot write failed", err);
  }
}

/** Write the signed-out placeholder snapshot (the widget shows
 *  "Open Sugalarm" instead of a stale number). Called on sign-out only —
 *  NOT on a stale feed (a stale feed self-ages to grey in the widget's
 *  own timeline; it stays signed-in). */
export async function clearGlucoseWidgetSnapshot(): Promise<void> {
  const p = plugin();
  if (!p) return;
  try {
    await p.writeWidgetSnapshot({ signedIn: false });
  } catch {
    /* best-effort */
  }
}

/**
 * Cycle 418 — the ActivityKit tokens this install has registered, so sign-out
 * can hand them back.
 *
 * ⚠ THE FIRST ATTEMPT ASKED THE BRIDGE, AND THE BRIDGE DOES NOT KNOW. It
 * called `getInfo()` and read `pushToken` / `startToken` off the result —
 * fields the shipped `LiveActivityBridge.getInfo` has never returned (it
 * resolves `available`, `enabled`, `pushToStartSupported`, `running` and
 * nothing else). It threw no error and logged nothing; it just handed back
 * `undefined` forever, so every release body carried an EMPTY live-activity
 * list and the §5.8 "no archive needed" claim was true only because the code
 * did nothing. A guard that checks a bridge METHOD EXISTS does not check that
 * it returns the FIELD you read.
 *
 * The tokens do not need the bridge: ActivityKit pushes them to
 * `wireTokenListener` above, which is where they are POSTed to
 * `/api/live-activity/tokens` in the first place. Persisting them there means
 * sign-out can name them on the SHIPPED 0.138.0 shell, which is the whole
 * point of §5.8 — no archive, the fix lands on the Fly deploy.
 */
const LA_TOKENS_KEY = "sugalarm.liveActivityTokens";

function rememberLiveActivityToken(token: string): void {
  try {
    const prior = rememberedLiveActivityTokens();
    if (prior.includes(token)) return;
    // Bounded: a device re-registers on every activity start, and an
    // unbounded list would grow for the life of the install.
    const next = [...prior, token].slice(-8);
    localStorage.setItem(LA_TOKENS_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable */
  }
}

export function rememberedLiveActivityTokens(): string[] {
  try {
    const raw = localStorage.getItem(LA_TOKENS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((t): t is string => typeof t === "string" && !!t)
      : [];
  } catch {
    return [];
  }
}

export function forgetLiveActivityTokens(): void {
  try {
    localStorage.removeItem(LA_TOKENS_KEY);
  } catch {
    /* storage unavailable */
  }
}
